import type { Step, CheckStatus, Rgb, Separation, SepMode, Ink, MockupJob, MockupTemplate, PrepareOptions, PrintArea } from '@eip/engine';

/** What the UI knows about an open document; the pixels stay in the worker. */
export interface DocMeta {
  id: string;
  name: string;
  width: number;
  height: number;
  dpi: number;
  colorProfile: 'srgb' | 'icc' | 'unknown';
  bitDepth: 8 | 16;
  kind: 'png' | 'psd' | 'bitmap';
  /** True when the file carried no density and 300 dpi was assumed (JPG without JFIF/EXIF density, WebP). */
  dpiAssumed?: boolean;
}

export interface StepProgress { index: number; kind: Step['kind']; ms: number; info?: Record<string, number>; warnings?: string[] }

export interface RunResult {
  generation: number;
  cancelled: boolean;
  width: number;
  height: number;
  dpi: number;
  warnings: string[];
  steps: StepProgress[];
}

/** A CheckResult without its mask: the mask stays in the worker, addressed by id. */
export interface CheckLite { id: string; label: string; status: CheckStatus; value: number; unit: string; message: string; hasOverlay: boolean }

export interface InspectLite { presetId: string; garment: Rgb; garmentTolerance: number; targetWidthIn?: number }

export type Which = 'source' | 'output';

/** `width`/`height` are the FULL-RES dimensions; the bitmap is `factor`× smaller. */
export interface PreviewResult { bitmap: ImageBitmap; factor: number; width: number; height: number }

export interface PlateMeta { id: string; kind: 'ink' | 'underbase' | 'highlight'; inkId?: string; name: string; coverage: number; width: number; height: number }
export interface SepBuildResult { generation: number; cancelled: boolean; plates: PlateMeta[]; checks: CheckLite[]; unmatched: number; indexSize?: { width: number; height: number }; ms: number }
export interface SepBuildInput { separation: Separation; garment: Rgb; artDpi: number; printWidthIn: number }
/** 1-bit PNG bytes, transferred. */
export interface FilmInfo { plateId: string; name: string; index: number; total: number; widthPx: number; heightPx: number; widthIn: number; heightIn: number; bytes: Uint8Array }
export interface FilmsExportResult { generation: number; cancelled: boolean; pdf: Uint8Array; proof: Uint8Array; count: number }

/** A prepared template: what the UI needs to describe it, with the maps left in the worker. */
export interface MockupPrepared {
  templateId: string;
  /** The worker-wide preparation counter's value at the time these maps were built — bumped every
   *  time `mockupLoadTemplate`/`mockupSaveEditor` actually (re)prepares a template (not when a
   *  cached one is handed back untouched). Lets a caller holding a `PreviewMaps` tell whether it
   *  still matches the template it was built from, by comparing the two `prepGeneration`s, without
   *  diffing every field either carries. */
  prepGeneration: number;
  /** The PREPARED photo's size — `photoFromBytes` caps the long side at `PHOTO_MAX_SIDE`, so
   *  this can be smaller than the file the caller sent. Print-area coordinates are in it. */
  width: number;
  height: number;
  /** Fraction of the photo the garment mask covers (0–1): under 10 % or over 90 % means the mask went wrong. */
  coverage: number;
  /** False when garment and backdrop could not be told apart by colour — no recolouring for this blank. */
  separable: boolean;
  garmentColor: Rgb;
  /** Every area actually used, primary first, each with the id and name `prepareTemplate` puts on
   *  it: the template's (or the editor's) own when it sent any, else the single one auto-derived
   *  from the mask. What the template editor seeds its area list from. */
  printAreas: PrintArea[];
  /** The primary area — `printAreas[0]`, by identity. Kept as a field of its own for every caller
   *  that knows one area (the runner, the viewer's transform box). */
  printArea: PrintArea;
  lmid: number;
  ms: number;
}

/** `mockupPrepareEditor`'s extra payload. Both bitmaps are the same size — the photo downscaled
 *  to ≤ `MOCKUP_PREVIEW_MAX` — so the editor can lay one straight over the other. */
export interface MockupEditorPrepared extends MockupPrepared { preview: ImageBitmap; maskEdge: ImageBitmap }

/** The maps and downscaled photo a WebGL preview renderer needs to draw a template without a CPU
 *  composite per frame — the same five arrays `renderMockup` reads, plus the photo, sized to
 *  `maxDim` (capped at `MOCKUP_PREVIEW_MAX`) instead of the template's full resolution. Nothing is
 *  cached in the worker: `mockupPreviewMaps` rebuilds these on every call, and it is the CALLER
 *  (a later task) that remembers them across frames.
 *
 *  `width`/`height` are exactly what `imageToBitmap(photo, Math.min(maxDim, MOCKUP_PREVIEW_MAX))`
 *  would produce for the template's full-res photo — the same size as the CPU preview bitmap the
 *  viewer already gets from `mockupRender` — and `factor` is `fullWidth / width` (`fullHeight /
 *  height`), so a caller holding a full-res coordinate (a print-area drag) can convert. `areas` are
 *  the template's print areas at THIS scale (see `scaleAreas`); ids, names and inches are unchanged.
 *
 *  `prepGeneration` echoes `MockupPrepared.prepGeneration` for the SAME template at the moment these
 *  maps were built, so a caller can tell whether they still match the `MockupPrepared` it is
 *  holding without diffing every field of either. */
export interface PreviewMaps {
  templateId: string;
  prepGeneration: number;
  width: number;
  height: number;
  /** `fullWidth / width` (and `fullHeight / height`) — 1 when the template photo already fits
   *  `maxDim` (true for the built-in templates against `MOCKUP_PREVIEW_MAX`). */
  factor: number;
  /** The photo, downscaled to `width` × `height` (RGBA). */
  photo: ImageBitmap;
  mask: Uint8Array;
  lumaBlur: Float32Array;
  gx: Int8Array;
  gy: Int8Array;
  residual: Int8Array;
  /** The garment's drop shadow at this scale (`PreparedMaps.shadow`), present iff the template's
   *  mask was separable. Its ABSENCE is what tells the GPU preview to ignore the job's backdrop,
   *  exactly as it makes `renderMockup` ignore one: a blank whose garment cannot be told from its
   *  backdrop has no silhouette to cast a shadow, and repainting behind it would eat the model. */
  shadow?: Uint8Array;
  lmid: number;
  areas: PrintArea[];
  garmentColor: Rgb;
  recolorable: boolean;
}

/** `width`/`height` are the FULL-RES dimensions (the template photo's); the bitmap is ≤ `maxDim`.
 *  `skipped` names the areas of the job's extra prints the render could NOT print — an area this
 *  blank does not have, or a document that is closed (or has no output yet) — de-duplicated. */
export interface MockupRenderResult { generation: number; cancelled: boolean; bitmap: ImageBitmap | null; width: number; height: number; clipped: boolean; skipped: string[]; ms: number }

/** The art-free garment: `width`/`height` are the FULL-RES photo's, the bitmap is ≤ `maxDim`,
 *  exactly as `MockupRenderResult` reports them — so the viewer can draw one in place of the
 *  other without a second coordinate space. */
export interface MockupBlankResult { bitmap: ImageBitmap; width: number; height: number }

/** `skipped` as `MockupRenderResult` reports it; a sheet's is the union over its tiles. */
export interface MockupExportResult { bytes: Uint8Array; width: number; height: number; clipped: boolean; skipped: string[]; mime: 'image/png' | 'image/jpeg' }
export type MockupFormat = 'png' | 'jpeg';

/** A backdrop image the worker has decoded and is holding: its size AFTER `photoFromBytes` (capped
 *  at `PHOTO_MAX_SIDE` like a template photo), which is what the panel shows and the store keeps. */
export interface SceneLoaded { width: number; height: number }

export interface EngineApi {
  open(id: string, name: string, bytes: Uint8Array): Promise<DocMeta>;
  close(id: string): Promise<void>;
  /** `onStep` is optional: Comlink has to open a MessageChannel for it, so a caller
   *  that ignores per-step progress should omit it rather than proxy a no-op. */
  run(id: string, steps: Step[], dpi: number, generation: number, onStep?: (p: StepProgress) => void): Promise<RunResult>;
  preview(id: string, which: Which, maxDim: number): Promise<PreviewResult | null>;
  /** `generation` is the inspect's OWN monotonic token, tracked separately from a run's:
   *  two inspections of the same output can overlap (a second preset change while the
   *  first is still streaming), and the later one must supersede the earlier. */
  inspect(id: string, which: Which, settings: InspectLite, generation: number, onCheck: (c: CheckLite) => void): Promise<CheckLite[]>;
  overlay(id: string, which: Which, checkId: string, maxDim: number, color: Rgb): Promise<ImageBitmap | null>;
  exportPng(id: string): Promise<{ bytes: Uint8Array; width: number; height: number; dpi: number }>;
  detectInks(id: string, count: number, mode: SepMode): Promise<Ink[]>;
  buildSeparation(id: string, input: SepBuildInput, generation: number): Promise<SepBuildResult>;
  /** Replace the separation the worker uses for film layout/screening and previews
   *  without rebuilding plates; clears the films cache. Ink changes still need
   *  `buildSeparation`.
   *
   *  Returns the separation checks recomputed against the new screening settings
   *  (moiré, mesh-vs-LPI and art-resolution all read `screen`), from the plates the
   *  last build left in the worker — no pixel work. `null` when the doc has no build
   *  yet, so the caller leaves the checks it has alone. */
  updateSeparation(id: string, separation: Separation): Promise<{ checks: CheckLite[] } | null>;
  /** Black ink on white, RGBA. */
  platePreview(id: string, plateId: string, maxDim: number): Promise<PreviewResult | null>;
  /** `compositeProof` over the garment used at build time. */
  proofPreview(id: string, maxDim: number): Promise<PreviewResult | null>;
  /** Renders+caches the film for that plate at 1:1, returns a crop. */
  filmCrop(id: string, plateId: string, x: number, y: number, w: number, h: number): Promise<{ bitmap: ImageBitmap; filmW: number; filmH: number } | null>;
  exportFilms(id: string, generation: number, onFilm: (f: FilmInfo) => void): Promise<FilmsExportResult>;
  /** `encodePngAsync(compositeProof(...))` at ≤ `maxDim`, transferred. */
  proofPng(id: string, maxDim: number): Promise<Uint8Array>;
  /** Prepare a template's mask and shading maps and cache them under `template.id`. Idempotent:
   *  a second call with the same id re-prepares only when `needsReprepare` says the mask, shading,
   *  print area or photo size moved — a rename or a colour-name edit just replaces the stored
   *  template. Throws when the photo's long side is under `PHOTO_MIN_SIDE`. */
  mockupLoadTemplate(template: MockupTemplate, photoBytes: Uint8Array): Promise<MockupPrepared>;
  /** Prepare an in-progress template for the editor, keyed by `editorId` rather than a template id.
   *  `photoBytes` null reuses the photo already stored under `editorId` (every control change
   *  re-prepares with new `opts`), and throws when there is none. */
  mockupPrepareEditor(editorId: string, photoBytes: Uint8Array | null, opts: PrepareOptions): Promise<MockupEditorPrepared>;
  /** Move the editor's photo and maps under `template.id`, re-preparing first if the template's
   *  own settings differ from the last `mockupPrepareEditor` call's. Drops the editor entry. */
  mockupSaveEditor(editorId: string, template: MockupTemplate): Promise<MockupPrepared>;
  /** Drop a prepared template or a cancelled editor (the same call serves both). */
  mockupUnload(templateId: string): Promise<void>;
  /** Decode a backdrop image and cache it under `sceneId`, flattened on white and capped at
   *  `PHOTO_MAX_SIDE` exactly like a template photo. Loading an id again REPLACES what is there
   *  (and drops the covers cut from the old pixels), so it is safe to call for a scene the worker
   *  may already hold. Every job naming this scene renders its backdrop from here. */
  mockupLoadScene(sceneId: string, bytes: Uint8Array): Promise<SceneLoaded>;
  /** Drop a scene and every photo-sized cover cut from it. */
  mockupUnloadScene(sceneId: string): Promise<void>;
  /** One scene scaled to cover `width × height` exactly, as a bitmap — the texture the GPU preview
   *  uploads, cut by the same `coverImage` the CPU render reads pixel for pixel, which is what
   *  keeps the two identical. Cached per (scene, size) beside the compositor's own covers. Throws
   *  'scene not loaded' when `sceneId` names a scene the worker does not hold. */
  mockupSceneCover(sceneId: string, width: number, height: number): Promise<ImageBitmap>;
  /** The prepared template's maps and photo, downscaled for the WebGL preview renderer (see
   *  `PreviewMaps`) — rebuilt fresh on every call, transferred out. Throws 'template not prepared'
   *  when `templateId` names a template the worker has not (yet) prepared. */
  mockupPreviewMaps(templateId: string, maxDim: number): Promise<PreviewMaps>;
  /** Composite the doc's DTF output into `job`'s template at the photo's full size, then downscale
   *  the returned bitmap to ≤ `maxDim`. Nothing is cached per document: the runner holds the bitmap. */
  mockupRender(docId: string, job: MockupJob, maxDim: number, generation: number): Promise<MockupRenderResult>;
  /** The job's blank with its garment recolour and NO art on it, downscaled like `mockupRender`.
   *  What the viewer paints under a placement drag's ghost: the art moves per frame, the garment
   *  under it does not, so one of these replaces a render per frame. Ungenerationed — it depends
   *  only on the template and the colour, and the caller caches it under exactly those. */
  mockupBlank(docId: string, job: MockupJob, maxDim: number): Promise<MockupBlankResult>;
  /** The same render at full resolution, encoded — PNG at 300 dpi, or JPEG at quality 0.9. */
  mockupExport(docId: string, job: MockupJob, format: MockupFormat): Promise<MockupExportResult>;
  /** A colourway sheet: one full-size render per job, tiled at `tileW` with `labels` under each
   *  (pass an empty array for no captions), encoded like `mockupExport`.
   *
   *  `docIds` lets each job name the document whose output it prints — that is what makes a
   *  front+back pair one worker call rather than two exports composed on the main thread. It
   *  must be as long as `jobs`; omitted, every tile prints `docId`. */
  mockupSheet(docId: string, jobs: MockupJob[], labels: string[], format: MockupFormat, tileW: number, docIds?: string[]): Promise<MockupExportResult>;
  /** The same composite as `mockupExport`, downscaled to `maxDim` on its long side and encoded
   *  as a quality-0.8 JPEG: the job ticket's thumbnail grid, small enough to inline as a
   *  `data:` URL. Transferred. */
  mockupThumb(docId: string, job: MockupJob, maxDim: number): Promise<Uint8Array>;
}

/** A result is stale when a newer run has been requested since it started. */
export function isStale(generation: number, latest: number): boolean { return generation < latest; }

export const PREVIEW_MAX = 2048;
/** Mockup previews render the whole garment photo, so they cap lower than `PREVIEW_MAX`. */
export const MOCKUP_PREVIEW_MAX = 1536;
